import { useEffect, useState, SetStateAction, useRef } from "react";
import { get } from "lodash";
import payService from "@tvg/api/pay";
import { useSelector } from "react-redux";
import { Dispatch } from "redux";
import { updateBalance } from "@tvg/shared-actions/UserActions";
import * as mediatorClassic from "@tvg/mediator-classic/src";
import { useIsomorphicLayoutEffect } from "@tvg/custom-hooks";
import {
  PaymentType,
  NullaryFn,
  UnaryFn,
  PaypalActions,
  AccountNumber,
  ErrorMessage
} from "@tvg/wallet/src/types";
import {
  gtmAuthorizeDepositSuccess,
  gtmAuthorizeDeposit,
  gtmDepositNewPaymentMethod,
  gtmAuthorizeDepositError
} from "@tvg/wallet/src/gtm";
import { getAccountNumber } from "@urp/store-selectors";
import { getBraintreePayPalToggle } from "../redux/toggles";
import { BRAINTREE_ERROR_CODES, PAYPAL_ERROR_CODES } from "../constants";
import type {
  BraintreeClientInstance,
  BraintreeDataCollectorInstance
} from "../types/braintree";

declare global {
  interface Window {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    paypal: any;
  }
}
// NOTE: `window.braintree` is typed globally as `BraintreeGlobal` in
// ../types/braintree.ts (shared with the Braintree card flow). It is
// intentionally NOT redeclared here — a second `braintree` augmentation would
// conflict with that ambient declaration.

interface BraintreePayPalCheckoutInstance {
  tokenizePayment: (
    paymentData: { orderID: string },
    callback: (
      error: Error | null,
      payload: {
        nonce: string;
        details?: {
          payerId?: string;
          email?: string;
          [key: string]: unknown;
        };
        type?: string;
        [key: string]: unknown;
      }
    ) => void
  ) => void;
  teardown: () => Promise<void>;
}

export const setPaypalButton = (
  value: string,
  maxDepositAmount: number,
  minDepositAmount: number,
  paypalActions: PaypalActions | null,
  setPaypalActive: UnaryFn<boolean, void>
) => {
  if (paypalActions) {
    if (
      Number(value) <= Number(maxDepositAmount) &&
      Number(value) >= Number(minDepositAmount) &&
      Number(value) !== 0
    ) {
      paypalActions.enable();
      setPaypalActive(true);
    } else {
      paypalActions.disable();
      setPaypalActive(false);
    }
  }
};

export const getPaypalToken = async (
  setIsLoading: UnaryFn<boolean, void>,
  accountId: string,
  value: string,
  setErrorMessage: React.Dispatch<SetStateAction<ErrorMessage>>,
  fromCreation: boolean
) => {
  try {
    setIsLoading(true);
    const {
      data: { token }
    } = await payService.getToken(accountId, value);
    gtmAuthorizeDeposit(fromCreation, "PAY", accountId);
    return token;
  } catch (err: unknown) {
    const errorCode = get(err, "response.data.code", "");

    setErrorMessage((prevState) => ({
      ...prevState,
      isOpen: true,
      errorCode,
      isRetry: false
    }));

    setIsLoading(false);
    throw err;
  }
};

export const finishPaypalDeposit = async (
  orderID: string,
  accountId: string,
  amount: string,
  setShowSuccessMessage: UnaryFn<boolean, void>,
  setIsLoading: UnaryFn<boolean, void>,
  dispatch: Dispatch,
  setErrorMessage: React.Dispatch<SetStateAction<ErrorMessage>>,
  pawsErrorMessages: Object,
  fromCreation: boolean,
  paymentType: PaymentType,
  errorMessageInitialState: ErrorMessage,
  refetchAvailableMethods: NullaryFn<void>
) => {
  try {
    const { data } = await payService.finishDeposit(accountId, orderID);
    if (data) {
      gtmAuthorizeDepositSuccess(fromCreation, "PAY", accountId, amount);
      dispatch(updateBalance(data.balance));
      mediatorClassic.dispatch("BALANCE_UPDATE", data.balance);
      setShowSuccessMessage(true);
    }

    if (fromCreation) {
      gtmDepositNewPaymentMethod(paymentType);
      refetchAvailableMethods();
    }
  } catch (err) {
    const errorCode = get(err, "response.data.code", "");
    const errorByCode =
      get(pawsErrorMessages, `${errorCode}`) ||
      get(pawsErrorMessages, "default");

    gtmAuthorizeDepositError(
      fromCreation,
      paymentType,
      accountId,
      get(errorByCode, "description", errorMessageInitialState.description)
    );

    setErrorMessage((prevState) => ({
      ...prevState,
      isOpen: true,
      errorCode,
      isRetry: false,
      paypalRetryMethod: () =>
        finishPaypalDeposit(
          orderID,
          accountId,
          amount,
          setShowSuccessMessage,
          setIsLoading,
          dispatch,
          setErrorMessage,
          pawsErrorMessages,
          fromCreation,
          paymentType,
          errorMessageInitialState,
          refetchAvailableMethods
        )
    }));
  }

  setIsLoading(false);
};

export const getBraintreeClientToken = async (
  accountId: string,
  setErrorMessage: React.Dispatch<SetStateAction<ErrorMessage>>
): Promise<{ clientToken: string; transactionUUID: string } | null> => {
  try {
    const response = await payService.getBraintreeClientToken(accountId);

    // Try both field names to handle potential API inconsistency
    const clientToken =
      get(response, "data.clientToken") || get(response, "data.token");
    const transactionUUID = get(response, "data.transactionUUID", "");

    if (!clientToken || typeof clientToken !== "string") {
      throw new Error("Invalid client token format");
    }

    return { clientToken, transactionUUID };
  } catch (err) {
    const errorCode = get(err, "response.data.code", "");

    setErrorMessage((prevState) => ({
      ...prevState,
      isOpen: true,
      errorCode,
      isRetry: false
    }));
  }

  return null;
};

/**
 * PayPal STC + RDA: fetch a fresh clientMetadataId for this deposit and
 * initialise the Braintree data collector with it, so the returned deviceData
 * carries the same correlation id the server registered via
 * CreateTransactionRiskContext. Must run on every PayPal deposit page load -
 * signals are never reused across deposits.
 */
export const collectPayPalDeviceData = async (
  accountId: string,
  clientInstance: BraintreeClientInstance,
  setErrorMessage: React.Dispatch<SetStateAction<ErrorMessage>>
): Promise<{
  deviceData: string;
  clientMetadataId: string;
  collector: BraintreeDataCollectorInstance;
} | null> => {
  try {
    const response = await payService.createTransactionRiskContext(accountId);
    const clientMetadataId = get(response, "data.clientMetadataId");

    if (!clientMetadataId || typeof clientMetadataId !== "string") {
      throw new Error("Invalid clientMetadataId");
    }

    if (!window.braintree?.dataCollector) {
      throw new Error("Braintree data collector not loaded");
    }

    const collector = await window.braintree.dataCollector.create({
      client: clientInstance,
      riskCorrelationId: clientMetadataId
    });

    if (!collector.deviceData) {
      collector.teardown().catch(() => {});
      throw new Error("Braintree returned no device data");
    }

    return { deviceData: collector.deviceData, clientMetadataId, collector };
  } catch (err) {
    setErrorMessage((prevState) => ({
      ...prevState,
      isOpen: true,
      errorCode:
        get(err, "response.data.code", "") || BRAINTREE_ERROR_CODES.INIT_ERROR,
      isRetry: false
    }));
  }

  return null;
};

export const finishBraintreeDeposit = async ({
  paymentMethodNonce,
  accountId,
  depositAmount,
  setShowSuccessMessage,
  setIsLoading,
  dispatch,
  setErrorMessage,
  pawsErrorMessages,
  fromCreation,
  paymentType,
  errorMessageInitialState,
  refetchAvailableMethods,
  transactionUUID,
  payerId,
  deviceData
}: {
  paymentMethodNonce: string;
  accountId: string;
  depositAmount: string;
  setShowSuccessMessage: UnaryFn<boolean, void>;
  setIsLoading: UnaryFn<boolean, void>;
  dispatch: Dispatch;
  setErrorMessage: React.Dispatch<SetStateAction<ErrorMessage>>;
  pawsErrorMessages: Object;
  fromCreation: boolean;
  paymentType: PaymentType;
  errorMessageInitialState: ErrorMessage;
  refetchAvailableMethods: NullaryFn<void>;
  transactionUUID?: string;
  payerId: string;
  deviceData: string;
}) => {
  try {
    gtmAuthorizeDeposit(fromCreation, "PAY", accountId);

    const { data } = await payService.finishBraintreeDeposit(
      accountId,
      paymentMethodNonce,
      depositAmount,
      transactionUUID,
      payerId,
      deviceData
    );

    if (data) {
      gtmAuthorizeDepositSuccess(fromCreation, "PAY", accountId, depositAmount);
      dispatch(updateBalance(data.balance));
      mediatorClassic.dispatch("BALANCE_UPDATE", data.balance);
      setShowSuccessMessage(true);
    }

    if (fromCreation) {
      gtmDepositNewPaymentMethod(paymentType);
      refetchAvailableMethods();
    }
  } catch (err) {
    const errorCode = get(err, "response.data.code", "");
    const errorByCode =
      get(pawsErrorMessages, `${errorCode}`) ||
      get(pawsErrorMessages, "default");

    gtmAuthorizeDepositError(
      fromCreation,
      paymentType,
      accountId,
      get(errorByCode, "description", errorMessageInitialState.description)
    );

    setErrorMessage((prevState) => ({
      ...prevState,
      isOpen: true,
      errorCode,
      isRetry: false,
      paypalRetryMethod: () =>
        finishBraintreeDeposit({
          paymentMethodNonce,
          accountId,
          depositAmount,
          setShowSuccessMessage,
          setIsLoading,
          dispatch,
          setErrorMessage,
          pawsErrorMessages,
          fromCreation,
          paymentType,
          errorMessageInitialState,
          refetchAvailableMethods,
          transactionUUID,
          payerId,
          deviceData
        })
    }));
  } finally {
    setIsLoading(false);
  }
};

export const usePaypalDeposit = (
  paymentType: PaymentType,
  value: string,
  fee: string,
  maxDepositAmount: number,
  minDepositAmount: number,
  isModalOpen: boolean,
  isLoading: boolean,
  _setShowAmountWarning: UnaryFn<boolean, void>,
  setErrorMessage: React.Dispatch<SetStateAction<ErrorMessage>>,
  pawsErrorMessages: Object,
  errorMessageInitialState: ErrorMessage,
  setShowSuccessMessage: UnaryFn<boolean, void>,
  setIsLoading: UnaryFn<boolean, void>,
  dispatch: Dispatch,
  fromCreation: boolean,
  refetchAvailableMethods: NullaryFn<void>
) => {
  const accountId: AccountNumber = useSelector(getAccountNumber);
  const isBraintreeEnabled = useSelector(getBraintreePayPalToggle);

  const [paypalActions, setPaypalActions] = useState<PaypalActions | null>(
    null
  );
  const [isPaypalButtonSet, setPaypalButtonSet] = useState<boolean>(false);
  const [isPaypalActive, setPaypalActive] = useState<boolean>(false);

  const valueRef = useRef(value);

  const [paypalCheckoutInstance, setPaypalCheckoutInstance] =
    useState<BraintreePayPalCheckoutInstance | null>(null);
  const [isBraintreeButtonSet, setIsBraintreeButtonSet] =
    useState<boolean>(false);
  const [braintreeActions, setBraintreeActions] =
    useState<PaypalActions | null>(null);
  const [isBraintreeActive, setIsBraintreeActive] = useState<boolean>(false);
  const transactionUUIDRef = useRef<string>("");
  const deviceDataRef = useRef<string>("");
  const riskCorrelationIdRef = useRef<string>("");
  const dataCollectorRef = useRef<BraintreeDataCollectorInstance | null>(null);
  // Bumped whenever the Braintree PayPal session is torn down (modal closed,
  // payment type changed, unmount). An initializeBraintree run that started
  // under an older value is stale and must not keep or render anything.
  const braintreeInitGenerationRef = useRef(0);
  const amountRef = useRef<string>("");
  const totalAmountRef = useRef<string>("");

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    if (!isBraintreeEnabled && paymentType === "PAY") {
      setPaypalButton(
        value,
        maxDepositAmount,
        minDepositAmount,
        paypalActions,
        setPaypalActive
      );
    }
  }, [
    value,
    paymentType,
    maxDepositAmount,
    minDepositAmount,
    paypalActions,
    isBraintreeEnabled
  ]);

  useEffect(() => {
    if (!isBraintreeEnabled && (!isModalOpen || paymentType !== "PAY")) {
      const container = document.getElementById("paypal-button-container");
      if (container) {
        container.innerHTML = "";
      }

      setPaypalButtonSet(false);
      setPaypalActive(false);
      setPaypalActions(null);
    }
  }, [isModalOpen, paymentType, isBraintreeEnabled]);

  useIsomorphicLayoutEffect(() => {
    if (
      !isBraintreeEnabled &&
      isModalOpen &&
      !isLoading &&
      !isPaypalButtonSet &&
      paymentType === "PAY" &&
      typeof window !== "undefined" &&
      window.paypal &&
      window.paypal.Buttons
    ) {
      setPaypalButtonSet(true);

      window.paypal
        .Buttons({
          fundingSource: window.paypal.FUNDING.PAYPAL,
          style: {
            label: "paypal",
            color: "gold",
            shape: "rect",
            height: 48
          },
          createOrder: () => {
            const currentValue = valueRef.current;
            return getPaypalToken(
              setIsLoading,
              accountId,
              currentValue,
              setErrorMessage,
              fromCreation
            ).catch((err) => {
              throw err;
            });
          },
          onApprove: (paypalData: { orderID: string }) =>
            finishPaypalDeposit(
              paypalData.orderID,
              accountId,
              valueRef.current,
              setShowSuccessMessage,
              setIsLoading,
              dispatch,
              setErrorMessage,
              pawsErrorMessages,
              fromCreation,
              paymentType,
              errorMessageInitialState,
              refetchAvailableMethods
            ),
          onCancel: () => {
            setIsLoading(false);
          },
          onError: () => {
            setErrorMessage((prevState) => ({
              ...prevState,
              isOpen: true,
              errorCode: PAYPAL_ERROR_CODES.PAYMENT_ERROR,
              isRetry: false
            }));
            setIsLoading(false);
          },
          onInit: (_data: unknown, actions: PaypalActions) => {
            setPaypalActions(actions);
            setPaypalButton(
              value,
              maxDepositAmount,
              minDepositAmount,
              actions,
              setPaypalActive
            );
          }
        })
        .render("#paypal-button-container");
    }
  }, [
    isModalOpen,
    isLoading,
    isPaypalButtonSet,
    paymentType,
    accountId,
    value,
    setErrorMessage,
    fromCreation,
    setShowSuccessMessage,
    setIsLoading,
    dispatch,
    pawsErrorMessages,
    errorMessageInitialState,
    refetchAvailableMethods,
    maxDepositAmount,
    minDepositAmount,
    isBraintreeEnabled
  ]);

  useEffect(() => {
    if (isBraintreeEnabled && paymentType === "PAY") {
      const baseAmount = Number(value).toFixed(2);
      const totalAmount = (Number(value) + Number(fee || 0)).toFixed(2);
      amountRef.current = baseAmount;
      totalAmountRef.current = totalAmount;

      const isValidAmount =
        Number(value) <= Number(maxDepositAmount) &&
        Number(value) >= Number(minDepositAmount) &&
        Number(value) !== 0;

      if (braintreeActions) {
        if (isValidAmount) {
          braintreeActions.enable();
        } else {
          braintreeActions.disable();
        }
      }

      setIsBraintreeActive(isValidAmount);
    }
  }, [
    value,
    fee,
    paymentType,
    maxDepositAmount,
    minDepositAmount,
    isBraintreeEnabled,
    braintreeActions
  ]);

  useEffect(
    () => () => {
      braintreeInitGenerationRef.current += 1;
    },
    []
  );

  useEffect(() => {
    if (isBraintreeEnabled && (!isModalOpen || paymentType !== "PAY")) {
      braintreeInitGenerationRef.current += 1;

      if (paypalCheckoutInstance) {
        paypalCheckoutInstance.teardown().catch(() => {});
        setPaypalCheckoutInstance(null);
      }

      // Device signals are per deposit: drop the collector and its deviceData
      // so the next PayPal page load collects fresh ones.
      if (dataCollectorRef.current) {
        dataCollectorRef.current.teardown().catch(() => {});
        dataCollectorRef.current = null;
      }
      deviceDataRef.current = "";
      riskCorrelationIdRef.current = "";

      const container = document.getElementById("paypal-button-container");
      if (container) {
        container.innerHTML = "";
      }

      setIsBraintreeButtonSet(false);
      setBraintreeActions(null);
    }
  }, [isModalOpen, paymentType, paypalCheckoutInstance, isBraintreeEnabled]);

  useIsomorphicLayoutEffect(() => {
    if (
      isBraintreeEnabled &&
      isModalOpen &&
      !isLoading &&
      !isBraintreeButtonSet &&
      paymentType === "PAY" &&
      typeof window !== "undefined" &&
      window.braintree &&
      window.braintree.client &&
      window.braintree.paypalCheckout
    ) {
      const initializeBraintree = async () => {
        setIsBraintreeButtonSet(true);

        const generation = braintreeInitGenerationRef.current;
        const isStale = () => generation !== braintreeInitGenerationRef.current;
        // A stale run must not raise an error modal over a closed or newer one.
        const setErrorIfCurrent: typeof setErrorMessage = (update) => {
          if (!isStale()) {
            setErrorMessage(update);
          }
        };
        let clientInstance: BraintreeClientInstance | null = null;
        let collector: BraintreeDataCollectorInstance | null = null;
        let paypalCheckout: BraintreePayPalCheckoutInstance | null = null;
        // Set once this run's collector, ids and checkout are published to the
        // hook's refs/state, so a later failure knows to withdraw them.
        let isCommitted = false;
        // Release whatever this run created, without touching shared refs or
        // state that a newer run may already own.
        const releaseRunResources = () => {
          paypalCheckout?.teardown().catch(() => {});
          collector?.teardown().catch(() => {});
          clientInstance?.teardown().catch(() => {});
        };

        try {
          const baseAmount = Number(value).toFixed(2);
          const totalAmount = (Number(value) + Number(fee || 0)).toFixed(2);
          amountRef.current = baseAmount;
          totalAmountRef.current = totalAmount;

          const container = document.getElementById("paypal-button-container");
          if (container) {
            container.innerHTML = "";
          }

          const tokenData = await getBraintreeClientToken(
            accountId,
            setErrorIfCurrent
          );

          if (isStale() || !tokenData) {
            return;
          }

          const { clientToken, transactionUUID } = tokenData;

          // Presence of braintree + paypalCheckout is guaranteed by the effect
          // guard above; re-capture locally so the type narrows inside this
          // async function (property narrowing does not flow into closures).
          const braintreeSdk = window.braintree;
          if (!braintreeSdk?.paypalCheckout) {
            return;
          }

          // Create Braintree client
          const client = await braintreeSdk.client.create({
            authorization: clientToken
          });
          clientInstance = client;

          if (isStale()) {
            releaseRunResources();
            return;
          }

          // PayPal STC + RDA: correlate this deposit's device data with the
          // server-side transaction risk context before rendering the button.
          const deviceDataResult = await collectPayPalDeviceData(
            accountId,
            client,
            setErrorIfCurrent
          );
          collector = deviceDataResult?.collector ?? null;

          if (isStale()) {
            releaseRunResources();
            return;
          }

          // Like a failed client-token fetch, stay unset until the modal is
          // reopened rather than retrying in a loop; the error modal is shown.
          if (!deviceDataResult) {
            client.teardown().catch(() => {});
            return;
          }

          // Create PayPal Checkout instance
          const paypalInstance = await braintreeSdk.paypalCheckout.create({
            client
          });
          paypalCheckout = paypalInstance;

          // Load the PayPal SDK through Braintree so it is configured with the
          // PayPal client-id linked to the Braintree gateway/merchant, rather
          // than the standalone legacy `paypalID` used by the static <script>.
          // This is the Braintree-recommended flow and prevents a
          // merchant/client-id mismatch at createPayment/tokenize time.
          await paypalInstance.loadPayPalSDK({
            currency: "USD",
            intent: "capture"
          });

          // Nothing has been kept from this run yet, so a session torn down
          // while it was pending leaves no stale button, collector or ids.
          if (isStale()) {
            releaseRunResources();
            return;
          }

          transactionUUIDRef.current = transactionUUID;
          dataCollectorRef.current?.teardown().catch(() => {});
          dataCollectorRef.current = deviceDataResult.collector;
          deviceDataRef.current = deviceDataResult.deviceData;
          riskCorrelationIdRef.current = deviceDataResult.clientMetadataId;
          setPaypalCheckoutInstance(paypalInstance);
          isCommitted = true;

          // Render PayPal button with Braintree integration
          window.paypal
            .Buttons({
              fundingSource: window.paypal.FUNDING.PAYPAL,
              style: {
                label: "paypal",
                color: "gold",
                shape: "rect",
                height: 48
              },
              createOrder: () =>
                // Use Braintree to create the order
                paypalInstance.createPayment({
                  flow: "checkout",
                  amount: totalAmountRef.current,
                  currency: "USD",
                  intent: "capture",
                  riskCorrelationId: riskCorrelationIdRef.current
                }),
              onApprove: (data: { orderID: string }) => {
                setIsLoading(true);
                // Wrap tokenizePayment in a Promise so PayPal SDK can properly await completion
                return new Promise<void>((resolve, reject) => {
                  paypalInstance.tokenizePayment(
                    data,
                    async (
                      tokenizeErr: Error | null,
                      payload: {
                        nonce: string;
                        details?: {
                          payerId?: string;
                          email?: string;
                          [key: string]: unknown;
                        };
                      }
                    ) => {
                      if (tokenizeErr) {
                        setErrorMessage((prevState) => ({
                          ...prevState,
                          isOpen: true,
                          errorCode: BRAINTREE_ERROR_CODES.PAYMENT_ERROR,
                          isRetry: false
                        }));
                        setIsLoading(false);
                        reject(tokenizeErr);
                        return;
                      }

                      const payerId = payload.details?.payerId;

                      if (!payerId) {
                        const error = new Error("Missing payer ID");
                        setErrorMessage((prevState) => ({
                          ...prevState,
                          isOpen: true,
                          errorCode: BRAINTREE_ERROR_CODES.MISSING_PAYER_ID,
                          isRetry: false
                        }));
                        setIsLoading(false);
                        reject(error);
                        return;
                      }

                      // Finish the deposit and await completion
                      try {
                        await finishBraintreeDeposit({
                          paymentMethodNonce: payload.nonce,
                          accountId,
                          depositAmount: amountRef.current,
                          setShowSuccessMessage,
                          setIsLoading,
                          dispatch,
                          setErrorMessage,
                          pawsErrorMessages,
                          fromCreation,
                          paymentType,
                          errorMessageInitialState,
                          refetchAvailableMethods,
                          transactionUUID: transactionUUIDRef.current,
                          payerId,
                          deviceData: deviceDataRef.current
                        });
                        resolve();
                      } catch (error) {
                        reject(error);
                      }
                    }
                  );
                });
              },
              onInit: (_data: unknown, actions: PaypalActions) => {
                setBraintreeActions(actions);
                const isValidAmount =
                  Number(value) <= Number(maxDepositAmount) &&
                  Number(value) >= Number(minDepositAmount) &&
                  Number(value) !== 0;

                if (isValidAmount) {
                  actions.enable();
                } else {
                  actions.disable();
                }
                setIsBraintreeActive(isValidAmount);
              },
              onCancel: () => {
                setIsLoading(false);
              },
              onError: () => {
                setErrorMessage((prevState) => ({
                  ...prevState,
                  isOpen: true,
                  errorCode: BRAINTREE_ERROR_CODES.PAYMENT_ERROR,
                  isRetry: false
                }));
                setIsLoading(false);
              }
            })
            .render("#paypal-button-container");
        } catch (err: unknown) {
          if (isStale()) {
            releaseRunResources();
            return;
          }
          // The current run failed: withdraw anything it already published so
          // the retry starts clean, then release what it created.
          if (isCommitted) {
            dataCollectorRef.current = null;
            deviceDataRef.current = "";
            riskCorrelationIdRef.current = "";
            transactionUUIDRef.current = "";
            setPaypalCheckoutInstance(null);
          }
          releaseRunResources();
          setIsBraintreeButtonSet(false);
          setErrorMessage((prevState) => ({
            ...prevState,
            isOpen: true,
            errorCode: BRAINTREE_ERROR_CODES.INIT_ERROR,
            isRetry: false
          }));
        }
      };

      initializeBraintree();
    }
  }, [
    isModalOpen,
    isLoading,
    isBraintreeButtonSet,
    paymentType,
    value,
    fee,
    minDepositAmount,
    maxDepositAmount,
    accountId,
    isBraintreeEnabled,
    fromCreation,
    setShowSuccessMessage,
    setIsLoading,
    dispatch,
    setErrorMessage,
    pawsErrorMessages,
    errorMessageInitialState,
    refetchAvailableMethods
  ]);

  if (isBraintreeEnabled) {
    return {
      isActive: isBraintreeActive,
      submitPayment: undefined, // Payment happens on button click, no separate submit needed
      isBraintreeEnabled: true
    };
  }
  return {
    isActive: isPaypalActive,
    submitPayment: undefined,
    isBraintreeEnabled: false
  };
};
